import React, { useEffect, useState } from 'react';
import { MapPin, Users, Ticket, Wallet, ArrowUpRight } from 'lucide-react';
import Card, { chartsData } from './DashboardFisrtBar/Card';
import axiosInstance from '../../axiosConfig';

const bookingsTotal = chartsData
  .find((row) => row.metric === 'Bookings');
const revenueTotal = chartsData
  .find((row) => row.metric === 'Revenue');

const sumRow = (row) => (row ? row.Tickets + row.Hotels + row['Train Tickets'] : 0);

const KpiCard = ({ icon: Icon, iconBg, iconColor, label, value, loading, delta }) => (
  <div
    style={{
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: 14,
      padding: '18px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>{label}</span>
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={18} color={iconColor} />
      </div>
    </div>
    <div style={{ fontSize: 26, fontWeight: 700, color: '#0f172a' }}>
      {loading ? '—' : value}
    </div>
    {delta && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#15803d', fontWeight: 600 }}>
        <ArrowUpRight size={12} />
        {delta}
      </div>
    )}
  </div>
);

export default function Dashboard() {
  const [destinationCount, setDestinationCount] = useState(null);
  const [userCount, setUserCount] = useState(null);
  const [loadingDestinations, setLoadingDestinations] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    let cancelled = false;

    axiosInstance
      .get('/internal-api/v1/client-web/destination-panel/config/list', { params: { start: 0, length: 1 } })
      .then((response) => {
        if (cancelled) return;
        const data = response.data || {};
        setDestinationCount(data.recordsFiltered ?? data.recordsTotal ?? data.total ?? 0);
      })
      .catch(() => {
        if (!cancelled) setDestinationCount(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingDestinations(false);
      });

    axiosInstance
      .get('/internal-api/v1/client-web/user-config/user-list', { params: { start: 0, length: 1 } })
      .then((response) => {
        if (cancelled) return;
        const data = response.data || {};
        setUserCount(data.recordsFiltered ?? 0);
      })
      .catch(() => {
        if (!cancelled) setUserCount(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 6px' }}>Dashboard</h1>
          <div style={{ fontSize: 13, color: '#64748b' }}>
            Home <span style={{ margin: '0 6px', color: '#94a3b8' }}>/</span>
            <span style={{ color: '#334155', fontWeight: 600 }}>Dashboard</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, color: '#64748b' }}>From</span>
          <input
            type="date"
            style={{ height: 38, border: '1px solid #e2e8f0', borderRadius: 8, padding: '0 12px', fontSize: 13, color: '#334155' }}
          />
          <span style={{ fontSize: 13, color: '#64748b' }}>To</span>
          <input
            type="date"
            style={{ height: 38, border: '1px solid #e2e8f0', borderRadius: 8, padding: '0 12px', fontSize: 13, color: '#334155' }}
          />
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 20,
          marginBottom: 28,
        }}
      >
        <KpiCard
          icon={MapPin}
          iconBg="rgba(0,181,236,0.14)"
          iconColor="#0055A5"
          label="Total Destinations"
          loading={loadingDestinations}
          value={destinationCount === null ? 'N/A' : destinationCount.toLocaleString()}
        />
        <KpiCard
          icon={Users}
          iconBg="rgba(110,190,69,0.16)"
          iconColor="#3f8f27"
          label="Total Users"
          loading={loadingUsers}
          value={userCount === null ? 'N/A' : userCount.toLocaleString()}
        />
        <KpiCard
          icon={Ticket}
          iconBg="rgba(217,119,6,0.14)"
          iconColor="#b45309"
          label="Total Bookings"
          loading={false}
          value={sumRow(bookingsTotal).toLocaleString()}
        />
        <KpiCard
          icon={Wallet}
          iconBg="rgba(0,145,142,0.14)"
          iconColor="#00706e"
          label="Total Revenue"
          loading={false}
          value={`LKR ${sumRow(revenueTotal).toLocaleString()}`}
        />
      </div>

      <Card />
    </div>
  );
}
